import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'

interface RpcResult {
  success: boolean
  error?: string
  from_status?: string
  to_status?: string
}

/**
 * Calls the Supabase RPC function to mark a user story as Test OK.
 * Transitions from 'testing' to 'done'.
 */
async function markTestOk(userStoryId: string): Promise<void> {
  const { data, error } = await supabase.rpc('mark_user_story_test_ok', {
    p_user_story_id: userStoryId,
  })

  if (error) {
    throw new Error(error.message)
  }

  const result = data as RpcResult
  if (!result.success) {
    throw new Error(result.error || 'Failed to mark test as OK')
  }
}

export function useTestOk() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markTestOk,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userStory'] })
      queryClient.invalidateQueries({ queryKey: ['userStories'] })
      queryClient.invalidateQueries({ queryKey: ['feature'] })
      queryClient.invalidateQueries({ queryKey: ['features'] })
    },
  })
}

interface TestKoParams {
  userStoryId: string
  issueDescription: string
}

/**
 * Calls the Supabase RPC function to mark a user story as Test KO.
 * Creates a message in exchange_messages with the issue description.
 */
async function markTestKo({ userStoryId, issueDescription }: TestKoParams): Promise<void> {
  const { data, error } = await supabase.rpc('mark_user_story_test_ko', {
    p_user_story_id: userStoryId,
    p_issue_description: issueDescription,
  })

  if (error) {
    throw new Error(error.message)
  }

  const result = data as RpcResult
  if (!result.success) {
    throw new Error(result.error || 'Failed to report issue')
  }
}

export function useTestKo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markTestKo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userStory'] })
      queryClient.invalidateQueries({ queryKey: ['userStories'] })
      queryClient.invalidateQueries({ queryKey: ['exchangeMessages'] })
    },
  })
}
